"use client"

import { useState, useRef, useEffect, useMemo } from "react"
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome"
import { faEdit, faTrash } from "@fortawesome/free-solid-svg-icons"
import Button from "../modules/Button/Button"
import Modal from "../modules/Modal/Moadal"
import Table from "../modules/table/table"
import DatePicker from "../../shared-modules/DatePicker/DatePicker"
import SerialInput from "../modules/SerialInput"
import Select from "../modules/Select/Select"
import { fetchByUrl } from "../../../services/inventory/detail"
import {
  updateWarehouseDevice,
  updateWarehouseBox,
  getDeviceTypes,
  deleteWarehouseDevice,
  getWarehouseDevices,
  bulkCreateWarehouseDevices,
  getWarehouseBoxDetail, 
} from "../../../services/inventory/detail"
import styles from "../../../styles/inventory/inventoryDetailsPage.module.css"

import MoveDeviceForm from "./MoveDeviceForm"
import { parseDateSafe } from "../../../utils/dateFormatters"

const MOVE_ALLOWED = new Set(["In Warehouse", "In Warehouse (Refurbished)"])
const EDIT_DISABLE_STATUSES = new Set(["Distributed"])

const DeviceDetailsModal = ({
  device,
  isOpen,
  onClose,
  editMode = false,
  selectedChildDevices = [],
  onChildSelectionChange = () => {},
  onBulkMoveSelected = () => {},
  onBulkMoveAll = () => {},
  isIndividualDevice = false,
  selected = false,
  onDataRefresh = () => {},
}) => {
  const [isEditMode, setIsEditMode] = useState(editMode)
  const [loading, setLoading] = useState(false)
  const [childLoading, setChildLoading] = useState(false)
  const [deviceTypes, setDeviceTypes] = useState([])

  const [childDevicesData, setChildDevicesData] = useState({ results: [], meta: {} })
  const [childDevicesLoading, setChildDevicesLoading] = useState(false)
  const [childDevicesKey, setChildDevicesKey] = useState(0)

  const [editData, setEditData] = useState({
    caseManager: "",
    date: "",
    email: "",
    deviceCount: "",
    deviceType: "",
    deviceTypeId: "",
    boxNumber: "",
    serialImei: "",
  })
  const [selectedDate, setSelectedDate] = useState(null)

  const [editingChildDevice, setEditingChildDevice] = useState(null)
  const [childEditData, setChildEditData] = useState({
    serialNumber: "",
    deviceType: "",
    deviceTypeId: "",
    caseManager: "",
    date: "",
    boxNumber: "",
    boxId: "",
  })

  const [confirmationModal, setConfirmationModal] = useState({
    isOpen: false,
    title: "",
    message: "",
    onConfirm: null,
    confirmText: "Delete",
    variant: "danger",
  })

  const [localSelectedIds, setLocalSelectedIds] = useState([])

  const [showMoveModal, setShowMoveModal] = useState(false)
  const [moveModalDevices, setMoveModalDevices] = useState([])
  const [moveModalIsBulk, setMoveModalIsBulk] = useState(false)

  const [showAddNewDevices, setShowAddNewDevices] = useState(false)
  const [newDevices, setNewDevices] = useState([])
  const [newDeviceErrors, setNewDeviceErrors] = useState([])
  const [addNewLoading, setAddNewLoading] = useState(false)

  const [isAdminState, setIsAdminState] = useState(true)
  useEffect(() => {
    try {
      const raw =
        typeof window !== "undefined"
          ? localStorage.getItem("inventory_active_account") || sessionStorage.getItem("inventoryActiveAccount")
          : null
      if (raw) {
        const acc = JSON.parse(raw)
        const isAdmin = String(acc?.type || "").toLowerCase().trim() === "admin" || acc?.type === ""
        setIsAdminState(Boolean(isAdmin))
      } else {
        setIsAdminState(true)
      }
    } catch {
      setIsAdminState(true)
    }
  }, [])

  useEffect(() => {
    if (!isOpen) {
      setShowAddNewDevices(false)
      setNewDevices([])
      setNewDeviceErrors([])
      setAddNewLoading(false)
      setEditingChildDevice(null)
      setLocalSelectedIds([])
    }
  }, [isOpen])

  useEffect(() => {
    if (device) {
      setEditData({
        caseManager: device.caseManager || device.added_by_name || "N/A",
        inventoryStatus: device.inventory_status ||  "N/A",
        date: device.date || (device.added_at ? parseDateSafe(device.added_at)?.toLocaleDateString() || "" : ""),
        email: device.email || "N/A",
        deviceCount: isIndividualDevice ? "1" : (device.quantity || "0").toString(),
        deviceType: device.type || device.device_type?.name || "",
        deviceTypeId: device.originalDevice?.device_type?.id?.toString() || "",
        boxNumber: device.serialRegNo || device.box_number || device.box?.box_number || "",
        serialImei: device.serialImei || device.serial || "",
        deliveredAt:device.deliveredAt||"N/A",
      })
      if (device.added_at) setSelectedDate(parseDateSafe(device.added_at))
    }
    setIsEditMode(editMode)
    setLocalSelectedIds([])

    setShowAddNewDevices(false)
    setNewDevices([])
    setNewDeviceErrors([])
    setAddNewLoading(false)
    setEditingChildDevice(null)
  }, [device, editMode, isIndividualDevice])

 const fetchChildDevices = async (pageOrUrlOrFilters = undefined) => {
  if (!isOpen || isIndividualDevice || !device?.id) {
    setChildDevicesData({ results: [], meta: {} })
    return
  }

  setChildDevicesLoading(true)

  try {
    let result = null

    if (typeof pageOrUrlOrFilters === "string" && pageOrUrlOrFilters.startsWith("http")) {
      result = await  fetchByUrl(pageOrUrlOrFilters)
    }

    else if (typeof pageOrUrlOrFilters === "number") {
      result = await getWarehouseDevices({
        box_id: device.id,
        page: pageOrUrlOrFilters,
      })
    }

    // ✅ Case 3: Filters object
    else if (typeof pageOrUrlOrFilters === "object" && pageOrUrlOrFilters !== null) {
      result = await getWarehouseDevices({
        box_id: device.id,
        ...pageOrUrlOrFilters,
      })
    }

    // ✅ Case 4: Initial load
    else {
      result = await getWarehouseDevices({ box_id: device.id })
    }

    // ✅ Normalize response
    const results = result?.results || result?.data?.results || []
    const meta = result?.meta || result?.data?.meta || {}

    setChildDevicesData({ results, meta })
  } catch (err) {
    console.error("Error fetching child devices:", err)
    setChildDevicesData({ results: [], meta: {} })
  } finally {
    setChildDevicesLoading(false)
  }
}


  useEffect(() => {
    fetchChildDevices()
  }, [isOpen, device?.id, isIndividualDevice, childDevicesKey])

  const handleChildPageChange = async (pageOrUrl) => {
    setChildDevicesLoading(true)
    try {
      if (typeof pageOrUrl === "string") await fetchChildDevices(pageOrUrl)
      else if (typeof pageOrUrl === "number") await fetchChildDevices({ page: pageOrUrl })
    } catch (err) {
      console.error("Error changing child page:", err)
    } finally {
      setChildDevicesLoading(false)
    }
  }

  const childDevices = useMemo(() => {
    if (isIndividualDevice) return []
    if (!Array.isArray(childDevicesData.results) || childDevicesData.results.length === 0) return []

    return childDevicesData.results
      .filter((d) => d && d.id)
      .map((d) => ({
        ...d,
        id: d.id,
        added_by_name: d.added_by_name || d.added_by || "Unknown",
        device_type_name: d.device_type?.name || d.device_type_name || "Unknown Device Type",
        device_type: d.device_type,
        serial: d.serial || d.serialNumber || "N/A",
        date: d.created_at ? parseDateSafe(d.created_at)?.toLocaleDateString() || "N/A" : d.added_at ? parseDateSafe(d.added_at)?.toLocaleDateString() || "N/A" : "N/A",
        box_number: d.box?.box_number || d.box_number || "N/A",
        status: d.device_status || d.inventory_status || "In Warehouse",
        inventoryStatus: d.inventory_status || "In Warehouse",
      }))
  }, [childDevicesData, isIndividualDevice])

  useEffect(() => {
    if (isOpen && !isIndividualDevice && childDevices.length > 0) {
      if (selected) {
        const allIds = childDevices.map((child) => child.id)
        setLocalSelectedIds(allIds)
        onChildSelectionChange(allIds)
      } else {
        setLocalSelectedIds(selectedChildDevices || [])
      }
    }
  }, [isOpen, selected, isIndividualDevice, childDevices.length])

  useEffect(() => {
    if (isIndividualDevice && isOpen) {
      const fetchDeviceTypes = async () => {
        try {
          const res = await getDeviceTypes()
          if (res && Array.isArray(res)) {
            setDeviceTypes(res.map((t) => ({ value: t.id.toString(), label: t.name })))
          }
        } catch (err) {
          console.error("Error fetching device types:", err)
        }
      }
      fetchDeviceTypes()
    }
  }, [isOpen, isIndividualDevice])

  const [boxDetail, setBoxDetail] = useState(null)
  const fetchBoxDetail = async (boxId) => {
    if (!boxId) {
      setBoxDetail(null)
      return
    }
    try {
      const res = await getWarehouseBoxDetail(boxId)
      if (res && res.success) {
        setBoxDetail(res.data)
      } else {
        setBoxDetail(null)
      }
    } catch (err) {
      console.error("Error fetching box detail:", err)
      setBoxDetail(null)
    }
  }

 useEffect(() => {
  if (isOpen && !isIndividualDevice && device?.id) {
    fetchBoxDetail(device.id)
  } else {
    setBoxDetail(null)
  }
}, [isOpen, device?.id, isIndividualDevice])


  useEffect(() => {
    const anyModalOpen = isOpen || showMoveModal || Boolean(editingChildDevice) || confirmationModal.isOpen
    if (anyModalOpen) {
      const prev = document.body.style.overflow
      document.body.style.overflow = "hidden"
      return () => {
        document.body.style.overflow = prev || ""
      }
    } else {
      document.body.style.overflow = ""
    }
  }, [isOpen, showMoveModal, editingChildDevice, confirmationModal.isOpen])

  const handleEditChange = (field, value) => setEditData((prev) => ({ ...prev, [field]: value }))
  const handleDateChange = (date) => setSelectedDate(date)

  const boxInventoryStatus = useMemo(() => {
    if (!device) return "In Warehouse"
    const original = device.originalBox || device?.box || null
    return (
      original?.inventory_status ||
      device?.inventory_status ||
      device?.inventoryStatus ||
      device?.box?.inventory_status ||
      device?.box?.inventoryStatus ||
      "In Warehouse"
    )
  }, [device])

  const isBoxMovable = useMemo(() => MOVE_ALLOWED.has(boxInventoryStatus), [boxInventoryStatus])
  const isBoxEditable = useMemo(() => !EDIT_DISABLE_STATUSES.has(boxInventoryStatus), [boxInventoryStatus])

  const areSelectedMovable = useMemo(() => {
    if (!localSelectedIds || localSelectedIds.length === 0) return false
    const selectedItems = childDevices.filter((c) => localSelectedIds.includes(c.id))
    if (selectedItems.length === 0) return false
    return selectedItems.every((it) => MOVE_ALLOWED.has(it.inventoryStatus || it.status))
  }, [localSelectedIds, childDevices])

  const areAllChildsMovable = useMemo(() => {
    if (!childDevices || childDevices.length === 0) return false
    return childDevices.every((it) => MOVE_ALLOWED.has(it.inventoryStatus || it.status))
  }, [childDevices])

  // New derived flag: if box has zero devices, Move Box should be disabled.
  const boxHasNoDevices = childDevices.length === 0

  // Add devices disabled logic:
  const existingCount = childDevices.length || 0
  const MAX_PER_BOX = 15
  const canAddMoreDevices = existingCount < MAX_PER_BOX
  const remainingSlots = Math.max(0, MAX_PER_BOX - existingCount)

  const canMoveBoxWhenAllChildrenMovable = areAllChildsMovable
  const canMoveBoxButton = isBoxMovable

  const addDevicesDisabled = useMemo(() => {
    if (!isAdminState) return true
    if (!canAddMoreDevices) return true
    if (boxHasNoDevices) return false
    if (!canMoveBoxButton && !canMoveBoxWhenAllChildrenMovable) return true
    return false
  }, [isAdminState, canAddMoreDevices, boxHasNoDevices, canMoveBoxButton, canMoveBoxWhenAllChildrenMovable])

  const handleSaveEdit = async () => {
    if (!isAdminState) return

    if (!isIndividualDevice && !isBoxEditable) {
      alert("Editing disabled for this box due to its inventory status.")
      return
    }
    setLoading(true)
    try {
      if (isIndividualDevice) {
        const payload = { serial: editData.serialImei }
        if (editData.deviceTypeId) payload.device_type = editData.deviceTypeId
        const result = await updateWarehouseDevice(device.id, payload)
        if (result.success) {
          setIsEditMode(false)
          onDataRefresh()
        } else {
          console.error("Error updating device:", result.message)
        }
      } else {
        const payload = { box_number: editData.boxNumber }
        if (selectedDate) payload.added_at = selectedDate.toISOString()
        const result = await updateWarehouseBox(device.id, payload)
        if (result.success !== false && (result.success || result.id)) {
          setIsEditMode(false)
          onDataRefresh()
        } else {
          console.error("Error updating box:", result.message)
        }
      }
    } catch (error) {
      console.error("Error updating:", error)
    } finally {
      setLoading(false)
    }
  }

  const toggleEditMode = () => {
    if (!isAdminState) {
      alert("Only admins can edit device details")
      return
    }

    if (!isIndividualDevice && !isBoxEditable) {
      alert("Editing is disabled for this box while its status is Distributed.")
      return
    }
    setIsEditMode((prev) => {
      if (prev) {
        handleSaveEdit()
        return false
      }
      return true
    })
  }

  const handleEditChildDevice = (childDevice) => {
    if (!isAdminState) {
      alert("Only admins can edit child devices")
      return
    }

    const childStatus = childDevice.inventory_status || childDevice.device_status || childDevice.status
    if (EDIT_DISABLE_STATUSES.has(childStatus)) {
      alert("Editing is disabled for this device because its status is Distributed.")
      return
    }
    setEditingChildDevice(childDevice)
    const actual = childDevicesData.results.find((d) => d.id === childDevice.id)
    setChildEditData({
      serialNumber: childDevice.serial || childDevice.serialNumber || "",
      deviceType: childDevice.device_type_name || childDevice.deviceType || "",
      deviceTypeId: actual?.device_type?.id?.toString() || "",
      caseManager: childDevice.added_by_name || childDevice.added_by || "",
      date: childDevice.date || "",
      boxNumber: childDevice.box?.box_number || childDevice.box_number || "",
      boxId: actual?.box?.id?.toString() || "",
    })
  }

  const handleSaveChildDevice = async () => {
    if (!isAdminState || !editingChildDevice) return
    setChildLoading(true)
    try {
      const actual = childDevicesData.results.find((d) => d.id === editingChildDevice.id)
      if (!actual) return
      const payload = { serial: childEditData.serialNumber, device_type: childEditData.deviceTypeId }
      const result = await updateWarehouseDevice(actual.id, payload)
      if (result.success) {
        setEditingChildDevice(null)
        await fetchChildDevices()
        onDataRefresh()
      } else {
        console.error("Failed to update child device:", result.message)
      }
    } catch (error) {
      console.error("Error updating child device:", error)
    } finally {
      setChildLoading(false)
    }
  }

  const handleDeleteChildDevice = (childDevice) => {
    if (!isAdminState) {
      alert("Only admins can delete devices")
      return
    }
    setConfirmationModal({
      isOpen: true,
      title: "Delete Device",
      message: `Are you sure you want to delete device "${childDevice.serial || childDevice.serialNumber}" (${childDevice.device_type_name || childDevice.deviceType})? This action cannot be undone.`,
      onConfirm: async () => {
        setChildLoading(true)
        try {
          const actual = childDevicesData.results.find((d) => d.id === childDevice.id)
          if (actual) {
            const result = await deleteWarehouseDevice(actual.id)
            if (result.success) {
              await fetchChildDevices()
              onDataRefresh()
            } else {
              console.error("Failed to delete child device:", result.message)
            }
          }
        } catch (error) {
          console.error("Error deleting child device:", error)
        } finally {
          setChildLoading(false)
          setConfirmationModal({ isOpen: false, title: "", message: "", onConfirm: null })
        }
      },
      confirmText: "Delete",
      variant: "danger",
    })
  }

  const handleDeleteSelectedChildDevices = () => {
    if (!isAdminState) {
      alert("Only admins can delete devices")
      return
    }
    if (localSelectedIds.length === 0) return
    const selectedDevicesForDelete = childDevices.filter((child) => localSelectedIds.includes(child.id))
    const deviceNames = selectedDevicesForDelete.map((d) => d.serial || d.serialNumber).join(", ")
    setConfirmationModal({
      isOpen: true,
      title: "Delete Selected Devices",
      message: `Are you sure you want to delete ${localSelectedIds.length} selected devices (${deviceNames})? This action cannot be undone.`,
      onConfirm: async () => {
        setChildLoading(true)
        try {
          const deletePromises = localSelectedIds.map(async (childId) => {
            const actual = childDevicesData.results.find((d) => d.id === childId)
            if (actual) return await deleteWarehouseDevice(actual.id)
            return { success: false }
          })
          await Promise.all(deletePromises)
          setLocalSelectedIds([])
          onChildSelectionChange([])
          await fetchChildDevices()
          onDataRefresh()
        } catch (err) {
          console.error("Error during bulk delete:", err)
        } finally {
          setChildLoading(false)
          setConfirmationModal({ isOpen: false, title: "", message: "", onConfirm: null })
        }
      },
      confirmText: "Delete All",
      variant: "danger",
    })
  }

  const ChildActionButtons = ({ childDevice }) => {
    const childStatus = childDevice.inventory_status || childDevice.device_status || childDevice.status
    const canEditChild = isAdminState && !EDIT_DISABLE_STATUSES.has(childStatus)
    const canMoveChild = MOVE_ALLOWED.has(childStatus)
    return (
      <div className={styles.action_buttons}>
        <button
          className={styles.action_btn}
          onClick={(e) => {
            e.stopPropagation()
            if (!canEditChild) return
            handleEditChildDevice(childDevice)
          }}
          title={canEditChild ? "Edit" : "Edit (disabled)"}
          disabled={!canEditChild}
        >
          <FontAwesomeIcon icon={faEdit} />
        </button>
        <button
          className={styles.action_btn}
          onClick={(e) => {
            e.stopPropagation()
            if (!isAdminState) return
            handleDeleteChildDevice(childDevice)
          }}
          title={canEditChild ? "Delete" : "Delete (admins only)"}
          disabled={!canEditChild}
        >
          <FontAwesomeIcon icon={faTrash} />
        </button>
        {}
      </div>
    )
  }

  const childTableColumns = [
    {
      key: "added_by_name",
      label: "Case Manager",
      alignLeft: true,
      render: (row) => row.added_by_name || row.added_by || "Unknown",
    },
    {
      key: "device_type",
      label: "Device Type",
      alignLeft: true,
      render: (row) => row.device_type?.name || row.device_type_name || "Unknown",
    },
    { key: "serial", label: "Serial Number", alignLeft: true, render: (row) => row.serial || "N/A" },
    { key: "date", label: "Date", alignLeft: true, render: (row) => row.date || "N/A" },
    {
      key: "status",
      label: "Status",
      alignLeft: true,
      render: (row) => row.device_status || row.inventory_status || row.status || "Unknown",
    },
    {
      key: "actions",
      label: "Actions",
      sorting: false,
      render: (childDevice) => <ChildActionButtons childDevice={childDevice} />,
    },
  ]

  const openMoveModalWithDevices = (devicesToMove = [], isBulk = false) => {
    if (!isAdminState) {
      alert("Only admins can move devices")
      return
    }

    if (isBulk && devicesToMove.length === 1 && devicesToMove[0].id === device?.id) {
      if (boxHasNoDevices) {
        alert("Cannot move an empty box (no devices inside).")
        return
      }
      if (!isBoxMovable) {
        alert("This box cannot be moved due to its inventory status.")
        return
      }
    } else {
      const allMovable = devicesToMove.every((it) => MOVE_ALLOWED.has(it.inventoryStatus || it.status))
      if (!allMovable) {
        alert("One or more selected devices cannot be moved due to their inventory status.")
        return
      }
    }

    // Close the current device details modal before opening move modal (user requested behaviour)
    try {
      if (typeof onClose === "function") onClose()
    } catch (e) {
      console.warn("onClose failed while opening move modal:", e)
    }

    setMoveModalDevices(devicesToMove)
    setMoveModalIsBulk(isBulk)
    setShowMoveModal(true)
  }
  const closeMoveModal = () => {
    setShowMoveModal(false)
    setMoveModalDevices([])
    setMoveModalIsBulk(false)
    onDataRefresh()
  }

  const modalFooter = () => {
    if (!isAdminState) return null

    const canMoveSelectedButton = areSelectedMovable

    const canShowMoveBox = !boxHasNoDevices && (canMoveBoxButton || canMoveBoxWhenAllChildrenMovable)
    const canMoveBox = canShowMoveBox

    return (
      <div className={styles.modal_footer_buttons}>
        {!isIndividualDevice && !isEditMode && (
          <>
            {childDevices.length > 0 && localSelectedIds.length === childDevices.length ? (
              <Button
                variant="primary"
                size="small"
                onClick={() => {
                  if (!canMoveBox) {
                    alert("Box cannot be moved due to inventory status or because it is empty.")
                    return
                  }
                  openMoveModalWithDevices([device], true)
                }}
                disabled={!canMoveBox}
              >
                Move Box ({childDevices.length})
              </Button>
            ) : (
              <>
                <Button
                  variant="secondary"
                  size="small"
                  onClick={() => {
                    const selectedChildItems = childDevices.filter((child) => localSelectedIds.includes(child.id))
                    if (selectedChildItems.length === 0) return
                    if (!areSelectedMovable) {
                      alert("One or more selected devices cannot be moved due to their inventory status.")
                      return
                    }
                    openMoveModalWithDevices(selectedChildItems, false)
                    onBulkMoveSelected(selectedChildItems)
                  }}
                  disabled={!canMoveSelectedButton}
                >
                  Move Selected ({localSelectedIds.length})
                </Button>

                <Button
                  variant="primary"
                  size="small"
                  onClick={() => {
                    if (!canMoveBox) {
                      alert("Box cannot be moved due to inventory status or because it is empty.")
                      return
                    }
                    openMoveModalWithDevices([device], true)
                  }}
                  disabled={!canMoveBox}
                >
                  Move Box ({childDevices.length})
                </Button>
              </>
            )}
          </>
        )}
      </div>
    )
  }

  const modalTitle = isIndividualDevice
    ? `Device - ${editData.serialImei || device?.serial || ""}`
    : `Box - ${device?.id} (${device?.type || device?.device_type?.name || ""})`

  // ------------------ BOX COMPATIBLE TYPES (prefer boxDetail) ------------------
  const boxCompatibleTypes = useMemo(() => {
    // prefer the fetched box detail (server response)
    if (boxDetail && Array.isArray(boxDetail.box_type_compatible) && boxDetail.box_type_compatible.length > 0) {
      return boxDetail.box_type_compatible
    }
    // fallback to existing fields on device
    const list = device?.compatible_device_types || device?.device_type_compatible || []
    if ((!list || list.length === 0) && Array.isArray(childDevicesData.results) && childDevicesData.results.length > 0) {
      const first = childDevicesData.results[0]
      if (first?.device_type_compatible) return first.device_type_compatible
    }
    return list || []
  }, [boxDetail, device, childDevicesData.results])
  // ---------------------------------------------------------------------------

  const boxCompatibleTypeOptions = useMemo(
    () =>
      (Array.isArray(boxCompatibleTypes ? boxCompatibleTypes : []) ? boxCompatibleTypes : []).map((t) => ({
        label: t.name,
        value: t.id.toString(),
      })),
    [boxCompatibleTypes]
  )

  const canAddMore = canAddMoreDevices
  const remainingSlotsValue = remainingSlots

  const addNewDeviceRow = () => {
    if (!canAddMore) return
    if (newDevices.length >= remainingSlotsValue) return
    setNewDevices((prev) => [...prev, { serial: "", device_type: "", inventory_status: boxInventoryStatus }])
  }

  const removeNewDeviceRow = (idx) => {
    setNewDevices((prev) => prev.filter((_, i) => i !== idx))
    setNewDeviceErrors((prev) => prev.filter((_, i) => i !== idx))
  }

  const updateNewDeviceRow = (idx, field, value) => {
    setNewDevices((prev) => {
      const copy = [...prev]
      copy[idx] = { ...copy[idx], [field]: value, inventory_status: boxInventoryStatus }
      return copy
    })
    setNewDeviceErrors((prev) => {
      const copy = [...prev]
      if (copy[idx]) copy[idx][field] = ""
      return copy
    })
  }

  const validateNewDevices = () => {
    const errs = []
    const totalAfter = existingCount + newDevices.length
    if (totalAfter > MAX_PER_BOX) {
      return { ok: false, message: `Cannot add: box would exceed ${MAX_PER_BOX} devices (current ${existingCount}).` }
    }
    newDevices.forEach((d, idx) => {
      const rowErr = {}
      if (!d.serial || !String(d.serial).trim()) {
        rowErr.serial = "Serial / IMEI is required"
      } else {
        const dtObj = (boxCompatibleTypes || []).find((t) => String(t.id) === String(d.device_type))
        const dtName = dtObj?.name || ""
        if (dtName.toLowerCase().includes("hub")) {
          if (!/^[0-9A-Fa-f]{12}$/.test(String(d.serial).trim())) {
            rowErr.serial = "Gateway ID must be 12 hex characters"
          }
        } else {
          if (!/^\d{15}$/.test(String(d.serial).trim())) {
            rowErr.serial = "IMEI must be exactly 15 digits"
          }
        }
      }
      if (!d.device_type) rowErr.device_type = "Device type required"
      errs[idx] = rowErr
    })
    const hasAny = errs.some((r) => Object.keys(r).length > 0)
    return { ok: !hasAny, errors: errs }
  }

  const handleSaveNewDevices = async () => {
    if (!device || !device.id) return
    if (newDevices.length === 0) return

    const validation = validateNewDevices()
    if (!validation.ok) {
      if (validation.message) setNewDeviceErrors([{ general: validation.message }])
      else setNewDeviceErrors(validation.errors || [])
      return
    }

    setAddNewLoading(true)
    setNewDeviceErrors([])
    try {
      const payload = newDevices.map((d) => ({
        serial: d.serial,
        device_type: d.device_type,
        inventory_status: boxInventoryStatus,
        box_id: Number(device.id),
      }))

      const result = await bulkCreateWarehouseDevices(payload)

      if (result && result.success) {
        setNewDevices([])
        setShowAddNewDevices(false)
        await fetchChildDevices()
        onDataRefresh()
      } else if (result && !result.success) {
        if (Array.isArray(result.errors)) {
          const mapped = []
          result.errors.forEach((e) => {
            const idx = typeof e.index === "number" ? e.index : null
            if (idx !== null && mapped[idx]) mapped[idx][e.field] = e.message
            else if (idx !== null) mapped[idx] = { [e.field]: e.message }
          })
          setNewDeviceErrors(mapped)
        } else {
          setNewDeviceErrors([{ general: result.message || "Failed to create devices" }])
        }
      } else {
        setNewDeviceErrors([{ general: "Failed to add devices" }])
      }
    } catch (err) {
      console.error("Error creating devices:", err)
      setNewDeviceErrors([{ general: "Error creating devices" }])
    } finally {
      setAddNewLoading(false)
    }
  }

  const getDeviceTypeOptions = () => {
    const selectedDevice = childDevicesData.results.find((d) => d.id === editingChildDevice?.id)
    if (!selectedDevice?.device_type_compatible) return []
    return selectedDevice.device_type_compatible.map((dt) => ({ value: dt.id.toString(), label: dt.name }))
  }

  return (
    <>
      <Modal title={modalTitle} isOpen={isOpen} onClose={onClose} width="950px" height="80vh" footer={modalFooter()}>
        <div className={styles.modal_content}>
          <div className={styles.gray}>
            <div className={styles.device_details}>
              <div className={styles.edit_delete_buttons}>
                {isAdminState && (
                  <button
                    className={styles.edit_save_btn}
                    onClick={toggleEditMode}
                    title={isEditMode ? "Save" : "Edit"}
                    disabled={
                      loading ||
                      (!isIndividualDevice && !isBoxEditable) 
                    }
                  >
                    <FontAwesomeIcon icon={faEdit} />
                  </button>
                )}
              </div>

              <div className={styles.details_gridd}>
                <div className={styles.detail_card}>
                  <div className={styles.detail_title}>{isIndividualDevice ? "Device Information" : "Box Information"}</div>

                  <div className={styles.detail_row}>
                    <div className={styles.detail_item}>
                      <span className={styles.detail_label}>Uploaded by</span>
                      <span className={styles.detail_value}>{editData.caseManager}</span>
                    </div>

                    <div className={styles.detail_item}>
                      <span className={styles.detail_label}>Email</span>
                      <span className={styles.detail_value}>{editData.email}</span>
                    </div>
                    <div className={styles.detail_item}>
                      <span className={styles.detail_label}>Email</span>
                      <span className={styles.detail_value}>{editData.deliveredAt}</span>
                    </div>

                    {!isIndividualDevice && (
                      <>
                        <div className={styles.detail_item}>
                          <span className={styles.detail_label}>Inventory status</span>
                          <span className={styles.detail_value}>{boxInventoryStatus}</span>
                        </div>
                      </>
                    )}

                    <div className={styles.detail_item}>
                      <span className={styles.detail_label}>Device Type</span>
                      {isIndividualDevice && isEditMode ? (
                        <Select
                          options={deviceTypes}
                          value={editData.deviceTypeId}
                          onChange={(value) => {
                            const selectedType = deviceTypes.find((opt) => opt.value === value)
                            handleEditChange("deviceTypeId", value)
                            handleEditChange("deviceType", selectedType?.label || "")
                          }}
                          placeholder="Select Device Type"
                          disabled={loading}
                          width="100%"
                          height="40px"
                        />
                      ) : (
                        <span className={styles.detail_value}>{editData.deviceType}</span>
                      )}
                    </div>

                    {isIndividualDevice && (
                      <div className={styles.detail_item}>
                        <span className={styles.detail_label}>{editData.deviceType === "Tenovi Hub" ? "Gateway ID (Hub)" : "Serial/IMEI"}</span>
                        {isEditMode ? (
                          <SerialInput
                            value={editData.serialImei}
                            onChange={(val) => handleEditChange("serialImei", val)}
                            deviceType={editData.deviceType}
                            label={editData.deviceType === "Tenovi Hub" ? "Gateway ID (Hub)" : "Serial/IMEI"}
                            disabled={loading}
                          />
                        ) : (
                          <span className={styles.detail_value}>{editData.serialImei}</span>
                        )}
                      </div>
                    )}

                    {!isIndividualDevice && (
                      <>
                        <div className={styles.detail_item}>
                          <span className={styles.detail_label}>Tenovi Box Number</span>
                          {isEditMode ? (
                            <input type="text" value={editData.boxNumber} onChange={(e) => handleEditChange("boxNumber", e.target.value)} className={styles.edit_input} disabled={loading || !isBoxEditable} />
                          ) : (
                            <span className={styles.detail_value}>{editData.boxNumber}</span>
                          )}
                        </div>

                        <div className={styles.detail_item}>
                          <span className={styles.detail_label}>Added At</span>
                          {isEditMode ? (
                            <DatePicker selectedDate={selectedDate} onDateSelect={handleDateChange} placeholder="Select date" disableFutureDates={false} disabled={loading || !isBoxEditable} />
                          ) : (
                            <span className={styles.detail_value}>{editData.date}</span>
                          )}
                        </div>
                      </>
                    )}
                  </div>
                </div>
              </div>
            </div>

            <div className={styles.save_button}>
              {isEditMode && isAdminState && (
                <Button onClick={handleSaveEdit} className={styles.button} loading={loading} disabled={loading || (!isIndividualDevice && !isBoxEditable)}>
                  Save
                </Button>
              )}
            </div>
          </div>

          {!isIndividualDevice && (
            <div>
              <div style={{ marginTop: 16 }}>
                <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                  <div>
                    <strong>Devices in box:</strong> {existingCount} / {MAX_PER_BOX}
                  </div>

                  <div>
                    <Button
                      variant="secondary"
                      size="small"
                      onClick={() => {
                        if (addDevicesDisabled) {
                          if (!isAdminState) alert("Only admins can add devices")
                          else if (!canAddMore) alert(`Box already has ${existingCount} devices. Maximum is ${MAX_PER_BOX}.`)
                          else alert("Add devices disabled due to box inventory status.")
                          return
                        }
                        setShowAddNewDevices((s) => !s)
                        if (!showAddNewDevices) {
                          setNewDevices([{ serial: "", device_type: "", inventory_status: boxInventoryStatus }])
                        }
                      }}
                      disabled={addDevicesDisabled}
                    >
                      {showAddNewDevices ? "Cancel" : "Add Devices"}
                    </Button>
                  </div>
                </div>

                {showAddNewDevices && (
                  <div style={{ marginTop: 12, border: "1px solid #e5e7eb", padding: 12, borderRadius: 6 }}>
                    {newDeviceErrors && newDeviceErrors[0] && newDeviceErrors[0].general && (
                      <div style={{ color: "red", marginBottom: 8 }}>{newDeviceErrors[0].general}</div>
                    )}

                    {newDevices.map((row, idx) => (
                      <div key={idx} style={{ display: "flex", gap: 8, marginBottom: 8, alignItems: "center" }}>
                        <div style={{ flex: 2 }}>
                          <SerialInput
                            value={row.serial}
                            onChange={(val) => updateNewDeviceRow(idx, "serial", val)}
                            deviceType={(boxCompatibleTypes || []).find((t) => String(t.id) === String(row.device_type))?.name || ""}
                            label="Serial / IMEI"
                            disabled={addNewLoading}
                          />
                          {newDeviceErrors[idx] && newDeviceErrors[idx].serial && <div style={{ color: "red", fontSize: 12 }}>{newDeviceErrors[idx].serial}</div>}
                        </div>

                        <div style={{ flex: 1 }}>
                          <Select
                            options={boxCompatibleTypeOptions}
                            value={row.device_type}
                            onChange={(val) => updateNewDeviceRow(idx, "device_type", val)}
                            placeholder="Device Type"
                            width="100%"
                            height="40px"
                            disabled={addNewLoading}
                          />
                          {newDeviceErrors[idx] && newDeviceErrors[idx].device_type && <div style={{ color: "red", fontSize: 12 }}>{newDeviceErrors[idx].device_type}</div>}
                        </div>

                        <div style={{ width: 36, textAlign: "center" }}>
                          <button
                            type="button"
                            onClick={() => removeNewDeviceRow(idx)}
                            disabled={addNewLoading}
                            style={{ border: "none", background: "transparent", color: "#ef4444", fontSize: 20, lineHeight: 1, cursor: "pointer" }}
                            title="Remove"
                          >
                            ×
                          </button>
                        </div>
                      </div>
                    ))}

                    <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginTop: 8 }}>
                      <div style={{ color: "#6b7280", fontSize: 13 }}>Remaining slots: {remainingSlotsValue - newDevices.length}</div>

                      <div style={{ display: "flex", gap: 8 }}>
                        <Button size="small" onClick={addNewDeviceRow} disabled={addNewLoading || newDevices.length >= remainingSlotsValue}>
                          +
                        </Button>

                        <Button variant="primary" size="small" onClick={handleSaveNewDevices} loading={addNewLoading} disabled={addNewLoading || newDevices.length === 0}>
                          Add devices
                        </Button>
                      </div>
                    </div>
                  </div>
                )}
              </div>

              <div className={styles.related_devices} style={{ marginTop: 16 }}>
                {childDevicesLoading ? (
                  <div style={{ textAlign: "center", padding: "20px" }}>Loading devices...</div>
                ) : (!Array.isArray(childDevicesData.results) || childDevicesData.results.length === 0) ? (
                  <div style={{ textAlign: "center", padding: "20px", color: "#666" }}>No devices found in this box</div>
                ) : (
                  <Table
                    columns={childTableColumns}
                    data={{ results: childDevicesData.results, meta: childDevicesData.meta }}
                    height={300}
                    isRowClickable={false}
                    loading={childDevicesLoading || childLoading}
                    showCheckboxes={isAdminState}
                    pagination={true}
                    meta={childDevicesData.meta}
                    onPageChange={handleChildPageChange}
                    onSelectionChange={(ids) => {
                      setLocalSelectedIds(ids)
                      onChildSelectionChange(ids)
                    }}
                    selectedIds={localSelectedIds}
                  />
                )}
              </div>
            </div>
          )}
        </div>
      </Modal>

      {editingChildDevice && (
        <Modal title="Edit Device" isOpen={!!editingChildDevice} onClose={() => setEditingChildDevice(null)} width="600px" height="auto">
          <div className={styles.child_edit_form}>
            <form
              onSubmit={(e) => {
                e.preventDefault()
                handleSaveChildDevice()
              }}
            >
              <div className={styles.form_row}>
                <div className={styles.form_group}>
                  <label className={styles.label}>Serial Number</label>
                  <SerialInput
                    value={childEditData.serialNumber}
                    onChange={(val) => setChildEditData((p) => ({ ...p, serialNumber: val }))}
                    deviceType={childEditData.deviceType}
                    label={childEditData.deviceType === "Tenovi Hub" ? "Gateway ID (Hub)" : "Serial/IMEI"}
                    disabled={childLoading || !isAdminState}
                  />
                </div>

                <div className={styles.form_group}>
                  <label className={styles.label}>Device Type</label>
                  <Select
                    options={getDeviceTypeOptions()}
                    value={childEditData.deviceTypeId}
                    onChange={(value) => {
                      const selectedType = getDeviceTypeOptions().find((opt) => opt.value === value)
                      setChildEditData((p) => ({ ...p, deviceTypeId: value, deviceType: selectedType?.label || "" }))
                    }}
                    placeholder="Select Device Type"
                    disabled={childLoading || !isAdminState}
                    width="100%"
                    height="40px"
                  />
                </div>
              </div>

              <div className={styles.form_row}>
                <div className={styles.form_group}>
                  <label className={styles.label}>Case Manager</label>
                  <input type="text" value={childEditData.caseManager} className={styles.input} disabled readOnly />
                </div>
                <div className={styles.form_group}>
                  <label className={styles.label}>Tenovi Number</label>
                  <input type="text" value={childEditData.boxNumber} className={styles.input} disabled readOnly />
                </div>
              </div>

              <div className={styles.button_container}>
                <Button type="button" variant="secondary" size="small" onClick={() => setEditingChildDevice(null)} disabled={childLoading}>
                  Cancel
                </Button>
                <Button type="submit" variant="primary" size="small" loading={childLoading} disabled={childLoading || !isAdminState}>
                  Save Device
                </Button>
              </div>
            </form>
          </div>
        </Modal>
      )}

      <MoveDeviceForm devices={moveModalDevices} isBulkMove={moveModalIsBulk} isOpen={showMoveModal} onClose={closeMoveModal} />

      <Modal
        isOpen={confirmationModal.isOpen}
        onClose={() => setConfirmationModal({ isOpen: false, title: "", message: "", onConfirm: null })}
        title={confirmationModal.title}
        width="500px"
        height="auto"
        footer={
          <div style={{ display: "flex", gap: "12px", justifyContent: "flex-end" }}>
            <Button variant="secondary" onClick={() => setConfirmationModal({ isOpen: false, title: "", message: "", onConfirm: null })} disabled={loading || childLoading}>
              Cancel
            </Button>
            <Button variant={confirmationModal.variant} onClick={confirmationModal.onConfirm} loading={loading || childLoading} disabled={loading || childLoading}>
              {confirmationModal.confirmText}
            </Button>
          </div>
        }
      >
        <p style={{ margin: 0, lineHeight: "1.5" }}>{confirmationModal.message}</p>
      </Modal>
    </>
  )
}

export default DeviceDetailsModal
